import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import JudgeCard from "@/components/JudgeCard";
import { Search, Users, MapPin, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", 
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", 
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", 
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", 
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", 
  "New Hampshire", "New Jersey", "New Mexico", "New York", 
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", 
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", 
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", 
  "West Virginia", "Wisconsin", "Wyoming"
];

export default function JudgesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedState, setSelectedState] = useState(searchParams.get("state") || "");
  const [selectedEventType, setSelectedEventType] = useState(searchParams.get("event") || "");
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJudges();
  }, [searchParams]);

  const fetchJudges = async () => {
    setLoading(true);
    try {
      const search = searchParams.get("search") || "";
      const state = searchParams.get("state") || "";
      const event = searchParams.get("event") || "";
      
      const response = await axios.get(`${API}/judges`, {
        params: { 
          search: search || state || undefined,
          limit: 2000,
          sort_by_lastname: true
        },
      });
      
      // Filter results based on state and event type
      let filteredJudges = response.data;
      
      if (state) {
        filteredJudges = filteredJudges.filter(judge => 
          judge.location?.toLowerCase().includes(state.toLowerCase())
        );
      }
      
      if (event) {
        filteredJudges = filteredJudges.filter(judge => 
          judge.events_judged?.includes(event)
        );
      }
      
      setJudges(filteredJudges);
    } catch (error) {
      console.error("Failed to fetch judges:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = {};
    if (searchQuery.trim()) params.search = searchQuery;
    if (selectedState) params.state = selectedState;
    if (selectedEventType) params.event = selectedEventType;
    setSearchParams(params);
  };

  const handleStateChange = (value) => {
    setSelectedState(value === "all" ? "" : value);
    const params = {};
    if (searchQuery.trim()) params.search = searchQuery;
    if (value && value !== "all") params.state = value;
    if (selectedEventType) params.event = selectedEventType;
    setSearchParams(params);
  };

  const handleEventTypeChange = (value) => {
    setSelectedEventType(value === "all" ? "" : value);
    const params = {};
    if (searchQuery.trim()) params.search = searchQuery;
    if (selectedState) params.state = selectedState;
    if (value && value !== "all") params.event = value;
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedState("");
    setSelectedEventType("");
    setSearchParams({});
  };

  const hasActiveFilters = searchParams.get("search") || searchParams.get("state") || searchParams.get("event");

  return (
    <div className="min-h-screen bg-off-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              Browse Judges
            </h1>
            <p className="text-slate-600 mt-1">
              {judges.length} judge{judges.length !== 1 ? "s" : ""} found
            </p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 space-y-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search judges by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-12 pl-12 pr-4 border-2 border-slate-200 focus:border-navy"
                data-testid="judges-search-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Select value={selectedState || "all"} onValueChange={handleStateChange}>
                <SelectTrigger className="h-12 w-48 border-2 border-slate-200" data-testid="state-filter-select">
                  <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {US_STATES.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedEventType || "all"} onValueChange={handleEventTypeChange}>
                <SelectTrigger className="h-12 w-48 border-2 border-slate-200" data-testid="event-filter-select">
                  <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by Event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="Hunt Test">Hunt Test</SelectItem>
                  <SelectItem value="Field Trial">Field Trial</SelectItem>
                </SelectContent>
              </Select>

              <Button
                type="submit"
                className="h-12 px-6 bg-navy hover:bg-navy/90"
                data-testid="judges-search-btn"
              >
                Search
              </Button>
              
              {hasActiveFilters && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 px-6"
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                >
                  Clear
                </Button>
              )}
            </div>
          </form>
          
          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <span className="text-slate-500">Filtering by:</span>
              {searchParams.get("search") && (
                <span className="bg-navy/10 text-navy px-3 py-1 rounded-full">
                  Name: "{searchParams.get("search")}"
                </span>
              )}
              {searchParams.get("state") && (
                <span className="bg-gold/20 text-navy px-3 py-1 rounded-full flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {searchParams.get("state")}
                </span>
              )}
              {searchParams.get("event") && (
                <span className="bg-gold/20 text-navy px-3 py-1 rounded-full flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {searchParams.get("event")}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white animate-pulse h-48 rounded-sm shadow"
              />
            ))}
          </div>
        ) : judges.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {judges.map((judge) => (
              <JudgeCard key={judge.id} judge={judge} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-sm shadow">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-navy mb-2">
              No judges found
            </h3>
            {hasActiveFilters ? (
              <>
                <p className="text-slate-600 mb-4">
                  No judges match your current filters
                </p>
                <Button onClick={clearFilters} variant="outline">
                  Clear filters
                </Button>
              </>
            ) : (
              <p className="text-slate-600">
                No judges available yet. Check back soon!
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-heading text-xl text-gold mb-2">RateMyJudges.org</p>
          <p className="text-slate-400 text-sm mb-4">
            The community-driven platform for AKC Hunt Test & Field Trial judge reviews.
          </p>
          <div className="border-t border-slate-700 pt-4 mt-4">
            <p className="text-slate-400 text-sm">
              <span className="text-gold font-medium">Contact Us:</span>{" "}
              <a 
                href="mailto:ratemyjudges@gmail.com" 
                className="text-white hover:text-gold transition-colors"
              >
                ratemyjudges@gmail.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
